import React from 'react';

import { convertArrayToCSV } from 'convert-array-to-csv';
import { EChartsInstance } from 'echarts-for-react';
import SaveAsImage from 'echarts/lib/component/toolbox/feature/SaveAsImage';
import FileSaver from 'file-saver';
import { debounce, groupBy } from 'lodash';
import { isEmpty, merge } from 'lodash/fp';
import numeral from 'numeral';

import _ from '../../locale';
import { LOCATION_ID_KEY, LOWER_KEY, UPPER_KEY, VALUE_KEY, YEAR_KEY } from '../../models/data-key';
import { styled } from '../../theme';
import echartsTheme from '../../theme/echarts-theme';
import { csvIcon, gifIcon } from '../../theme/icons';
import { DataGranularityKey, DataRecord } from '../../types';
import getDataResponseTimeUnits from '../../utility/data/time-units/get-data-response-time-units';
import getTitleAndSubtitle from '../../utility/echarts-helpers/get-adopted-title-and-subtitle';
import getTimelineConfig from '../../utility/echarts-helpers/get-time-slider-config';
import isPlayFunctionEnabled from '../../utility/echarts-helpers/is-time-slider-play-enabled';
import { formatValue } from '../../utility/filters-helpers';
import { convertENotationToDecimal } from '../../utility/formatting/convert-e-notation-to-decimal';
import { ChartZoomConsumer, Context as ChartZoomContext } from '../ChartZoomProvider';
import EChartsWrapper from '../EChartsWrapper';
import MapChartColorScaleControls from '../MapChartColorScaleControls';
import { ErrorBox, FormGroup } from '../index';
import { isMapRegistered } from './utils';

const CHART_MOBILE_BREAKPOINT = 768;
const E_NOTATION_TO_DECIMAL_PRECISION = 10;

const ChartWrapper = styled('div')({
    display: 'flex',
});

type Props = ChartZoomContext & {
    renderTitle?: () => void;
    renderSubtitle?: () => void;
    renderTooltip?: () => void;
    enableSliderWithFilterKey?: string;
    initialSliderValue?: number;
    onSliderChange?: () => void;
    valueKey?: string;
    additionalTitles?: any;
    saveAsGIF?: {
        visible?: boolean;
        filename?: () => void;
        icon?: string;
        title?: string;
        onClick?: () => void;
    };
    saveAsImage?: {
        visible?: boolean;
        enabled?: boolean;
        filename?: () => void;
        icon?: string;
        title?: string;
        onClick?: () => void;
        excludeComponents?: string[];
    };
    saveAsCSV?: {
        visible?: boolean;
        filename?: () => void;
        icon?: string;
        title?: string;
        headers?: Array<string> | (() => string);
        keys?: Array<string> | (() => string);
    };
    visualMapConfig?: {
        show?: boolean;
        min?: number;
        max?: number;
        range?: Array<number>;
        realtime?: boolean;
        calculable?: boolean;
        top?: number;
        itemWidth?: number;
        inRange?: { color: any };
        outOfRange?: { color: any };
    };
    theme: object;
    height: number;
    generateLocationParams?: (record: DataRecord) => any;

    colorScaleConfig?: {
        show: boolean;
        showScaleControls?: boolean;
        generatePalette: (
            min: number,
            max: number,
            rangeMin: number,
            rangeMax: number,
            sliderValue: number
        ) => string[];
    };
    valueGetter: ({
        record,
        valueIdx,
        locationIdx,
    }: {
        record: DataRecord;
        valueIdx: number;
        locationIdx: number;
    }) => string | number | (string | number)[];
    timeUnitKey: DataGranularityKey;
    initialZoom: number;
    isLoadingData: boolean;
    echartsMapKey: string;
    mapElementsAmount: number;
};

type State = {
    sliderValue: number | string;
    sliderIndex: number;
    chartWidth: number;
    windowWidth: number;
    timelinePlayState: boolean;
};

const MAX_SUPPORTED_TIMEUNIT_AMOUNT = 1394;

class MapChart extends React.PureComponent<Props, State> {
    static defaultProps = {
        valueKey: VALUE_KEY,
        timeUnitKey: YEAR_KEY,
        renderTitle: () => '',
        valueGetter: ({ record, valueIdx }) => record[valueIdx],
        renderSubtitle: () => '',
        saveAsGIF: {
            visible: false,
            filename: () => 'Chart_Animation',
            icon: '',
            title: '',
        },
        saveAsImage: {
            visible: false,
            filename: () => 'Chart_ScreenShot',
            icon: '',
            title: '',
        },
        saveAsCSV: {
            visible: false,
            filename: () => 'Chart_CSV',
            icon: '',
            title: '',
            headers: [],
            keys: [],
        },
        theme: {},
        additionalTitles: {},
        colorScaleConfig: { show: false },
        initialZoom: 0.8,
        mapElementsAmount: 0,
    };

    state: State = {
        sliderValue: this.props.initialSliderValue,
        chartWidth: window.innerWidth,
        windowWidth: window.innerWidth,
        timelinePlayState: false,
    };

    theme: any;
    chartInstance = null;

    constructor(props) {
        super(props);

        this.theme = merge(echartsTheme, props.theme);

        this.onWindowResize = debounce(this.onWindowResize.bind(this), 250);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        const { initialSliderValue, isLoadingData } = this.props;
        const { sliderValue } = this.state;

        if (!isLoadingData) {
            const sliderOptions = this.getTimelineOptions();
            if (!isEmpty(sliderOptions) && !sliderOptions.includes(sliderValue)) {
                this.setState({ sliderValue: sliderOptions[sliderOptions.length - 1] });
            }
        }

        if (initialSliderValue !== prevProps.initialSliderValue) {
            this.setState({ sliderValue: initialSliderValue });
        }
    }

    componentDidMount() {
        window.addEventListener('resize', this.onWindowResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onWindowResize);
    }

    onChartReady = (instance: EChartsInstance) => {
        this.props.setZoomableInstance(instance);
        this.chartInstance = instance;
    };

    onChartResize = (width, height) => {
        this.setState({ chartWidth: width });
    };

    getVisualMapForColorScale = () => ({
        top: 30,
        itemHeight: 350,
        formatter: (value: number) => {
            let numberFormat = '0,0';

            if (value < 0.001) {
                numberFormat = '0.[00000]';
            } else if (value < 0.01) {
                numberFormat = '0.[0000]';
            } else if (value < 0.1) {
                numberFormat = '0.[000]';
            } else if (value < 1) {
                numberFormat = '0.[00]';
            } else if (value < 10) {
                numberFormat = '0.[0]';
            }

            return numeral(value).format(numberFormat);
        },
    });

    resetChartPosition = () => {
        const { echartsMapKey, initialZoom } = this.props;
        if (!this.chartInstance?.setOption) {
            return null;
        }

        this.chartInstance.setOption({
            series: [
                {
                    type: 'map',
                    map: echartsMapKey,
                    zoom: initialZoom,
                    center: null,
                },
            ],
        });
    };

    onWindowResize = () => {
        this.setState({ windowWidth: window.innerWidth });
    };

    isMobileView = () => this.state.windowWidth < CHART_MOBILE_BREAKPOINT;

    getChartHeight = () => (this.isMobileView() ? 350 : 450);

    getRenderProps = () => {
        const { filtersValues: filters, timeUnitKey } = this.props;
        const { sliderValue } = this.state;

        return { filters, sliderValue: formatValue(timeUnitKey, sliderValue, _) };
    };

    getChartData = () => {
        const {
            columns,
            records,
            valueKey,
            enableSliderWithFilterKey,
            generateLocationParams,
            valueGetter,
        } = this.props;
        if (isEmpty(records)) {
            return [];
        }

        const valueIdx = columns.indexOf(valueKey);
        const upperIdx = columns.indexOf(UPPER_KEY);
        const lowerIdx = columns.indexOf(LOWER_KEY);
        const locationIdx = columns.indexOf(LOCATION_ID_KEY);

        // if slider filter key is used we need to group data by that key
        const chartSeries = enableSliderWithFilterKey ? this.getChartDataSeries() : [records];
        // [ [[id, name, value],...], [[id, name, value],...] ]
        return Object.values(chartSeries).map((group) =>
            group.map((record) => ({
                id: record[locationIdx],
                name: record[locationIdx].toString(),
                value: valueGetter({ record, valueIdx, locationIdx }),
                [UPPER_KEY]: record[upperIdx],
                [LOWER_KEY]: record[lowerIdx],
                ...(generateLocationParams && generateLocationParams(record)),
            }))
        );
    };

    getChartDataSeries = () => {
        const { columns, records, enableSliderWithFilterKey } = this.props;
        const { sliderValue } = this.state;

        // if slider filter key is used we need to group data by that key
        const sliderIdx = columns.indexOf(enableSliderWithFilterKey);

        const sliderOptions = this.getTimelineOptions();
        if (isPlayFunctionEnabled(sliderOptions)) {
            return groupBy(records, (record) => record[sliderIdx]);
        }

        // when play function is disabled no need to pass all the data to series but only current time unit values
        const currentSliderValueData = records.filter(
            (record) => record[sliderIdx] === sliderValue
        );

        const sliderDataSeries = {};
        sliderOptions.forEach((option) => {
            sliderDataSeries[option] = option === sliderValue ? currentSliderValueData : [];
        });

        return sliderDataSeries;
    };

    getCSVData = () => {
        const { columns, records, enableSliderWithFilterKey, saveAsCSV } = this.props;
        const columnHeaders = saveAsCSV.headers;
        const columnsKeys = saveAsCSV.keys;

        const arrayRecords = records
            .filter((record) => {
                // when slider enabled filter using slider
                if (enableSliderWithFilterKey) {
                    return (
                        record[columns.indexOf(enableSliderWithFilterKey)] ===
                        this.state.sliderValue
                    );
                }

                return true;
            })
            .map((record) => [
                ...columnsKeys.map((key) => formatValue(record[columns.indexOf(key)], key, _)),
            ]);

        return convertArrayToCSV(arrayRecords, { header: columnHeaders });
    };

    getRangePickerMinValue = () => {
        const { columns, records, valueKey, enableSliderWithFilterKey, timeUnitKey } = this.props;
        const { sliderValue } = this.state;

        const valueIdx = columns.indexOf(valueKey);
        const timeUnitIdx = columns.indexOf(timeUnitKey);
        const currentTimeUnitRecords = enableSliderWithFilterKey
            ? records.filter((record) => record[timeUnitIdx] === sliderValue)
            : records;
        const min = Math.min(
            ...currentTimeUnitRecords.map((record) =>
                convertENotationToDecimal(record[valueIdx], E_NOTATION_TO_DECIMAL_PRECISION)
            )
        );
        return isFinite(min) ? min : 0;
    };

    getRangePickerMaxValue = () => {
        const { columns, records, valueKey, enableSliderWithFilterKey, timeUnitKey } = this.props;
        const { sliderValue } = this.state;

        const valueIdx = columns.indexOf(valueKey);
        const timeUnitIdx = columns.indexOf(timeUnitKey);
        const currentTimeUnitRecords = enableSliderWithFilterKey
            ? records.filter((record) => record[timeUnitIdx] === sliderValue)
            : records;
        const max = Math.max(
            ...currentTimeUnitRecords.map((record) =>
                convertENotationToDecimal(record[valueIdx], E_NOTATION_TO_DECIMAL_PRECISION)
            )
        );
        return isFinite(max) ? max : 0;
    };

    getTimelineOptions = (): number[] | string[] => {
        const { columns, records, enableSliderWithFilterKey } = this.props;

        const options = getDataResponseTimeUnits({ columns, records }, enableSliderWithFilterKey);

        const halfLength = Math.floor(options.length / 2);
        let i = 0;
        while (options.length > MAX_SUPPORTED_TIMEUNIT_AMOUNT) {
            options.splice(halfLength - i * 2, 1);
            i++;
        }

        return options;
    };

    handleSliderChange = (params) => {
        const sliderValue = this.getTimelineOptions()[params.currentIndex];
        this.setState({ sliderValue });

        this.props.onEvents?.timelinechanged?.(params);
    };

    handleTimelinePlayChanged = (params) => {
        this.setState({ timelinePlayState: params.playState });

        this.props.onEvents?.timelineplaychanged?.(params);
    };

    getToolboxFeatures = () => {
        const features = {
            saveAsImage: { show: false },
        };

        const { saveAsImage, saveAsCSV, saveAsGIF } = this.props;

        if (saveAsGIF?.visible) {
            features.mySaveAsGIF = this.getSaveAsGIFConfig();
        }

        if (saveAsImage?.visible) {
            features.mySaveAsImage = this.getMySaveAsImageConfig();
        }

        if (saveAsCSV?.visible) {
            features.mySaveAsCSV = this.getSaveAsCSVConfig();
        }

        return features;
    };

    getMySaveAsImageConfig = () => {
        const {
            visible,
            enabled,
            filename = (renderProps?) => 'Chart_ScreenShot',
            icon,
            title = _('chart_save_as_image_button_label'),
            onClick,
            excludeComponents,
        } = this.props.saveAsImage;

        if (!visible) {
            return undefined;
        }

        const config = {
            excludeComponents: excludeComponents || ['toolbox'],
            pixelRatio: 2,
            title: enabled ? title : '',
            name: filename(this.getRenderProps()),
            onclick: function (...params) {
                if (!enabled) {
                    return;
                }
                SaveAsImage.prototype.onclick.call(this, ...params);
                onClick && onClick();
            },
            ...(!enabled && {
                iconStyle: {
                    color: '#A2A3A4',
                },
                emphasis: {
                    iconStyle: {
                        color: '#A2A3A4',
                    },
                },
            }),
        };

        return icon ? { ...config, icon } : config;
    };

    getSaveAsCSVConfig = () => {
        const {
            visible,
            filename = () => 'Chart_Data',
            icon,
            title = _('chart_save_as_csv_button_label'),
        } = this.props.saveAsCSV;

        if (!visible) {
            return undefined;
        }

        return {
            icon: icon || csvIcon,
            title,
            onclick: () => {
                const name = filename(this.getRenderProps()) + '.csv';
                const data = new Blob([this.getCSVData()], {
                    type: 'text/plain',
                });
                FileSaver.saveAs(data, name);
            },
        };
    };

    getSaveAsGIFConfig = () => {
        const {
            visible,
            filename = () => 'Chart_Animation',
            icon,
            title = _('chart_save_as_gif_button_label'),
            enabled,
            onClick,
        } = this.props.saveAsGIF;

        if (!(visible && this.getTimelineOptions()?.length > 1)) {
            return { show: false };
        }

        return {
            show: true,
            icon: icon || gifIcon,
            title: enabled ? title : '',
            onclick: (...params) => {
                enabled && onClick?.(...params);
            },
            ...(!enabled && {
                iconStyle: {
                    color: '#A2A3A4',
                },
                emphasis: {
                    iconStyle: {
                        color: '#A2A3A4',
                    },
                },
            }),
        };
    };

    changeColorScale = () => {
        const {
            colorScaleConfig: { generatePalette },
        } = this.props;
        const { sliderValue } = this.state;

        const { min, max, rangeMin, rangeMax } = this.getVisualMapState();
        const palette = generatePalette(min, max, rangeMin, rangeMax, sliderValue);
        this.setVisualMapPalette(palette);
    };

    resetColorScale = () => {
        const {
            colorScaleConfig: { generatePalette },
        } = this.props;

        const { min, max } = this.getVisualMapState();
        const palette = generatePalette(min, max, min, max);
        this.setVisualMapPalette(palette);
    };

    getVisualMapState = (): {
        min: number;
        max: number;
        rangeMin: number;
        rangeMax: number;
    } => {
        if (this.chartInstance?.getOption) {
            const { min, max, range } = this.chartInstance.getOption().visualMap[0];
            return {
                min,
                max,
                rangeMin: range[0],
                rangeMax: range[1],
            };
        }

        return {
            min: 0,
            max: 0,
            rangeMin: 0,
            rangeMax: 0,
        };
    };

    setVisualMapPalette = (value: string[]) => {
        if (this.chartInstance?.setOption) {
            const { visualMapConfig } = this.props;

            const visualMap = [
                {
                    show: true,
                    realtime: true,
                    calculable: true,
                    itemWidth: 6,
                    inRange: {
                        color: value,
                    },
                    outOfRange: {
                        color: this.theme.map.locationOutOfRangeColor,
                    },
                    ...this.getVisualMapForColorScale(),
                },
            ];

            if (visualMapConfig) {
                visualMap.push(visualMapConfig);
            }

            this.chartInstance.setOption({
                visualMap,
            });
        }
    };

    getChartEvents = (): Record<string, Function> => ({
        ...this.props.onEvents,
        // please remember to check onEvents.someHandler in the following
        // events as it would be overwritten
        timelinechanged: this.handleSliderChange,
        timelineplaychanged: this.handleTimelinePlayChanged,
    });

    getChartOption = () => {
        const {
            isLoadingData,
            renderTitle,
            renderTooltip,
            renderSubtitle,
            visualMapConfig,
            colorScaleConfig,
            enableSliderWithFilterKey,
            echartsMapKey,
            mapElementsAmount,
            additionalTitles,
            customGraphicElements,
            columns,
            records,
        } = this.props;

        const { chartWidth, sliderValue, timelinePlayState } = this.state;

        let rangePickerMaxValue = this.getRangePickerMaxValue();
        let rangePickerMinValue = this.getRangePickerMinValue();
        let max = rangePickerMaxValue;
        let min = rangePickerMinValue;

        if (rangePickerMaxValue === rangePickerMinValue) {
            // only one uniq value is present (IHME-1140)
            max = rangePickerMaxValue + Math.max(1, rangePickerMinValue * 0.01);
            min = rangePickerMinValue - Math.max(1, rangePickerMinValue * 0.01);

            rangePickerMaxValue += 0.000001;
            rangePickerMinValue -= 0.000001;
        }
        const chartData = this.getChartData();
        const isDataEmpty = chartData.length === 0;

        const inRangeColor =
            colorScaleConfig && colorScaleConfig.generatePalette
                ? colorScaleConfig.generatePalette(min, max, min, max, sliderValue)
                : this.theme.map.locationInRangeColorSpectrum;

        const isMaxElementsAmountExceeded = mapElementsAmount > 3000;
        const visualMap = merge(
            {
                show: colorScaleConfig.show,
                min,
                max,
                range: [rangePickerMinValue, rangePickerMaxValue],
                realtime: true,
                calculable: true,
                itemWidth: 6,
                inRange: {
                    color: inRangeColor,
                },
                outOfRange: {
                    color: this.theme.map.locationOutOfRangeColor,
                },
                ...this.getVisualMapForColorScale(),
            },
            visualMapConfig
        );

        const renderProps = this.getRenderProps();

        return {
            baseOption: {
                timeline: {
                    ...getTimelineConfig(
                        enableSliderWithFilterKey,
                        sliderValue,
                        this.getTimelineOptions(),
                        this.theme,
                        chartWidth,
                        _
                    ),
                    autoPlay: timelinePlayState,
                },
                color: inRangeColor,
                visualMap,
                tooltip: {
                    position: this.isMobileView() ? [0, '50%'] : 'inside',
                    formatter: (location) => renderTooltip(location, renderProps),
                    show: true,
                    triggerOn: 'click',
                    alwaysShowContent: false,
                    backgroundColor: 'white',
                    enterable: true,
                },
                title: [
                    getTitleAndSubtitle(
                        renderTitle(renderProps),
                        renderSubtitle(renderProps),
                        chartWidth,
                        document.getElementById('canvasFontSizer'),
                        this.theme.title
                    ),
                    {
                        show: isDataEmpty && !isLoadingData,
                        text: _('chart_error_no_data'),
                        top: '45%',
                        textStyle: {
                            ...echartsTheme.textStyle,
                            fontSize: 26,
                        },
                    },
                    additionalTitles,
                ],
                toolbox: {
                    top: this.isMobileView() ? 20 : 0,
                    right: 30,
                    z: 10,
                    ...this.theme.toolbox,
                    feature: this.getToolboxFeatures(),
                },
                textStyle: this.theme.textStyle,
                graphic: {
                    elements:
                        customGraphicElements?.(
                            { columns, records },
                            this.chartInstance,
                            renderProps
                        ) || [],
                },
            },
            options: chartData.map((data, i) => ({
                series: [
                    {
                        data,
                        type: 'map',
                        map: echartsMapKey,
                        selectedMode: 'single',
                        height: '90%',
                        label: {
                            formatter: (data) => _(`location_${data.name}`),
                        },
                        itemStyle: {
                            areaColor: this.theme.map.locationDefaultColor,
                            borderWidth: 0.5,
                            borderColor: this.theme.map.locationBorderColor,
                        },
                        emphasis: {
                            label: {
                                color: this.theme.map.locationLabelColor,
                                fontSize: 12,
                            },
                            itemStyle: isMaxElementsAmountExceeded
                                ? {
                                      areaColor: 'transparent',
                                      borderWidth: 2,
                                      borderColor: 'rgba(80, 195, 247, 0.5)',
                                      shadowColor: 'rgb(80, 195, 247)',
                                      shadowBlur: 10,
                                  }
                                : {
                                      areaColor: this.theme.map.locationSelectedColor,
                                      borderWidth: 0.5,
                                      borderColor: this.theme.map.locationBorderColor,
                                  },
                        },
                        ...this.theme.map,
                    },
                ],
            })),
        };
    };

    render() {
        const { errors } = this.props;

        if (errors) {
            return (
                <FormGroup>
                    <ErrorBox errors={errors} />
                </FormGroup>
            );
        }

        const { isLoadingData, height, colorScaleConfig, echartsMapKey, initialSliderValue } =
            this.props;

        return (
            <>
                <ChartWrapper id="chart-wrapper">
                    <div style={{ width: '100%' }}>
                        {isMapRegistered(echartsMapKey) && (
                            <EChartsWrapper
                                key={initialSliderValue}
                                notMerge={false}
                                onChartReady={this.onChartReady}
                                onChartResize={this.onChartResize}
                                showLoading={isLoadingData}
                                loadingOption={this.theme.loading}
                                theme={this.theme}
                                style={{
                                    height: height ? height : this.getChartHeight(),
                                    width: '100%',
                                }}
                                onEvents={this.getChartEvents()}
                                option={this.getChartOption()}
                            />
                        )}
                    </div>
                </ChartWrapper>
                {colorScaleConfig &&
                    colorScaleConfig.show &&
                    colorScaleConfig.showScaleControls && (
                        <MapChartColorScaleControls
                            setScale={this.changeColorScale}
                            resetScale={this.resetColorScale}
                        />
                    )}
            </>
        );
    }
}

const ConnectedChart = ({ forwardRef, ...props }) => (
    <ChartZoomConsumer>
        {(zoomProps) => <MapChart {...zoomProps} {...props} ref={forwardRef} />}
    </ChartZoomConsumer>
);

export default React.forwardRef((props, ref) => <ConnectedChart {...props} forwardRef={ref} />);
